import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';

import PropTypes from 'prop-types';

import { Box, Checkbox, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { useTransl } from 'shared/lib/i18n';

import TextFieldComponent from '../../FormField/TextFieldComponent';
import SearchComponent from '../../Table/SearchComponent';

import { ActionButton } from '../../Button';
import { LinkCommunitySkeleton } from '../../Shared/skeletons';

const StyledUpdateComponent = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'end',
  },
}));

const StyledTextField = styled(TextFieldComponent)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginBottom: '1rem',
  },
}));

const CommunityListComponent = ({
  isLoading,
  setSearch,
  search,
  buildingList,
  filteredBuildingList,
  setBuildingList,
  onLinkCommunities,
  onUpdateCommunityName,
  communityName,
  setCommunityName,
  isLinking,
}) => {
  const { t } = useTransl();

  const toggleSelected = (item) => {
    const itemIndex = buildingList?.map(({ id }) => id)?.indexOf(item?.id);
    const newList = [...buildingList];
    newList.splice(itemIndex, 1, { ...item, isSelected: !item?.isSelected });
    setBuildingList(newList);
  };

  const buildingItem = ({ data, index, style }) => {
    const item = data?.[index];
    const isLastItem = index === Number(data?.length) - 1;
    const { isSelected, isDisabled, name } = item;

    const content = (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Checkbox
            onClick={() => toggleSelected(item)}
            checked={isSelected}
            disabled={isDisabled}
            color="primary"
            size="medium"
          />
          <Box flex={1}>{name}</Box>
        </Box>
        {!isLastItem && (
          <Box
            sx={{
              marginLeft: '1rem',
              width: '95%',
              border: '1px solid #DDDDDD',
            }}
          />
        )}
      </>
    );

    return <div style={style}>{content}</div>;
  };

  return (
    <Box sx={{ margin: '3rem 0 2rem 0' }}>
      <Typography sx={{ margin: '-2rem 0' }}>
        {t('community.communityName')}
      </Typography>
      <StyledUpdateComponent display="flex" my="2rem" alignItems="center">
        <StyledTextField
          input={{
            value: communityName,
            onChange: (e) => setCommunityName(e.target.value),
          }}
          meta={{}}
          placeholder={t('community.communityName')}
        />
        <Box ml="2rem" />
        <ActionButton
          variant="contained"
          disableElevation
          onClick={() => onUpdateCommunityName(communityName)}
          style={{ marginTop: '-4px', width: '8rem' }}
        >
          {t('actions.save')}
        </ActionButton>
      </StyledUpdateComponent>
      {isLinking && isLoading ? (
        <LinkCommunitySkeleton />
      ) : isLinking ? (
        <>
          <SearchComponent
            placeholder={t('title.search')}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            fullWidth
          />
          <Box sx={{ minHeight: '300px', margin: '2rem 0' }}>
            {filteredBuildingList?.length ? (
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    height={height}
                    itemData={filteredBuildingList}
                    itemCount={filteredBuildingList?.length}
                    itemSize={50}
                    width={width}
                  >
                    {buildingItem}
                  </List>
                )}
              </AutoSizer>
            ) : (
              <Box>{t('community.noResults')}</Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ActionButton
              variant="contained"
              disableElevation
              onClick={() => onLinkCommunities()}
            >
              {t('community.link')}
            </ActionButton>
          </Box>
        </>
      ) : null}
    </Box>
  );
};

CommunityListComponent.propTypes = {
  setSearch: PropTypes.func.isRequired,
  search: PropTypes.string.isRequired,
  setCommunityName: PropTypes.func.isRequired,
  communityName: PropTypes.string.isRequired,
  buildingList: PropTypes.instanceOf(Array).isRequired,
  filteredBuildingList: PropTypes.instanceOf(Array).isRequired,
  setBuildingList: PropTypes.func.isRequired,
  onLinkCommunities: PropTypes.func.isRequired,
  onUpdateCommunityName: PropTypes.func.isRequired,
  isLinking: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default CommunityListComponent;
